export default function AuthoritySection() {
  return (
    <section className="section-padding bg-navy-900 text-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight">
              Authority-driven development means your inventory data is always correct
            </h2>
            <p className="mt-6 text-lg text-navy-200">
              Unlike conventional WMS that lets errors drift across receipts, picks, and counts, Klarhead enforces inventory invariants at every movement. Every scan is validated. Every lot is reconcilable. Every audit trail is complete.
            </p>

            <div className="mt-8 space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold">Scan Verification</h3>
                  <p className="mt-1 text-navy-300">
                    Barcode and GS1 scans are validated at the system level. Picks, put-aways, and dispatches are checked against expected state — preventing errors before they reach the ledger.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold">Inventory Reconciliation</h3>
                  <p className="mt-1 text-navy-300">
                    Location balances, lot quantities, and expiry dates are continuously reconciled against movements. Discrepancies are flagged at cycle count — not discovered at audit.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold">Complete Audit Trails</h3>
                  <p className="mt-1 text-navy-300">
                    Every scan, movement, and state change is logged with timestamp, user, and context. Auditors and insurers get deterministic answers, not reconstructed narratives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="bg-navy-800 rounded-xl p-6 lg:p-8 border border-navy-700">
              <div className="text-sm font-medium text-navy-400 uppercase tracking-wide mb-4">
                System Invariants
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-navy-700">
                  <span className="text-navy-200">INV-I1: Location-Lot Balance</span>
                  <span className="flex items-center text-amber-400">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-navy-700">
                  <span className="text-navy-200">INV-I2: FIFO Enforcement</span>
                  <span className="flex items-center text-amber-400">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-navy-700">
                  <span className="text-navy-200">INV-I3: Expiry Date Guard</span>
                  <span className="flex items-center text-amber-400">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-navy-200">S4: Stock Release State Integrity</span>
                  <span className="flex items-center text-amber-400">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-navy-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-navy-400">Last verification</span>
                  <span className="text-navy-200">2 seconds ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
